import
{
    PaginatedResult,
    PaginationRequest,
} from "@ews/react-data";
import
{
    useL10n
} from "@ews/react-localization-context";
import
{
    IonCheckbox,
    IonCol,
    IonGrid,
    IonLabel,
    IonRow,
} from "@ionic/react";
import
{
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState
} from "react";
import FilterList, { isFilterActive } from "../../FilterList/FilterList";
import { FilterState } from "../../FilterList/types";
import List from "../../List/List";
import { defaultPageRange } from "../../List/Pagination/pagerange";
import { useMyDateFormat } from "../../Login/LoginProvider";
import { usePersistentState } from "../../Persistence";
import { useFetchUserSentNotificationsLog, useUserDetails } from "../../ReactData/user";
import { useIsMobile } from "../../Responsive";
import DatePicker, { dateRange, dateStringToValue, valueToDateString } from "../../Settings/SettingsComponents/SettingsRangeInput/components/DatePicker/DatePicker";
import { Filter } from "../../System/Reports/types/reportTypes";
import { NotificationType, SentNotification, SentNotificationFilterKeys, SentNotificationLogProps } from "./types";
import { HeaderOption } from "../../List/types";
import { Locale } from "../../../setup/locale";

import style from "./SentNotificationsLog.module.scss";

type PersistenceFilter = Filter<SentNotificationFilterKeys> & FilterState;

const defaultRange = dateRange(new Date(), 1);

const ignoreKeys: (keyof PersistenceFilter)[] = ["pageSize", "isFilterOpen", "page", "pageSize", "sortCriteria", "sortOrder"];

const SentNotificationsLog: React.FC<SentNotificationLogProps> = ({
    userId,
    filter,
    onIsOpen,
    onFilterActive
}) =>
{
    const { translate: t } = useL10n();

    const filterData = useFetchUserSentNotificationsLog();
    const mobileView = useIsMobile();
    const dateFormat = useMyDateFormat();
    const { timezone } = useUserDetails(userId || "");


    const header: HeaderOption<Locale, SentNotification>[] = useMemo(() => [

        {
            title: 'COM_FACPMANAGEMENT_SHARES_ANLAGENNAME',
            key: 'title',
            className: style.systemname,
            both: true,
        },
        {
            title: 'COM_CONTACT_CONTACT_ENTER_MESSAGE_LABEL',
            key: 'message',
            className: style.message,
            both: true,
        },
        {
            title: '[REACT_NOTIFICATION_TYPE]',
            key: 'notificationType',
            className: style.notificationType,
            both: true,
        },
        {
            title: '[REACT_RECEIVER]',
            key: 'emailOrToken',
            className: style.receiver,
            breakpoint: 1034,
            up: true,
            child: ({ emailOrToken, notificationType }) =>
            {
                return <>
                    {notificationType === 'email' ? emailOrToken : ''}
                </>;
            }
        },
        {
            title: '[REACT_SENDED]',
            key: 'created',
            className: style.created,
            breakpoint: 991,
            up: true,
            child: ({ created }) =>
            {
                return <>
                    {dateFormat(new Date(created!), timezone)}
                </>;
            }
        },
    ], [dateFormat, timezone]);

    const standardFilter: Filter<SentNotificationFilterKeys, keyof SentNotification> = useMemo(() => ({
        notificationType: ['email', 'pushNotification'].join(","),
        created: valueToDateString(defaultRange, timezone),
        sortCriteria: "created",
        sortOrder: "DESC",
        pageSize: defaultPageRange,
    }), [timezone]);

    const [result, setResult] = useState<PaginatedResult<SentNotification>>({
        currentPage: 0,
        pageSize: standardFilter.pageSize || defaultPageRange,
        totalCountOfEntries: 0,
        totalCountOfPages: 0,
        results: [],
    });

    const [currentState, setState] = usePersistentState<PersistenceFilter>('users:user-notifications-log', { ...standardFilter } as PersistenceFilter);
    const sortCriteria = useRef<{
        key: string;
        direction: "ASC" | "DESC";
    } | undefined>(undefined);


    const getFilteredData = useCallback(async (id: string, filter: PaginationRequest) =>
    {
        const response = await filterData(id, filter);
        if (!response) return;

        setResult(response);
    }, [filterData]);

    const onPageChange = useCallback((page: PersistenceFilter["page"]) => setState((prev) => ({ ...prev, page })), [setState]);
    const onSortChange = useCallback((sortCriteria: PersistenceFilter["sortCriteria"], sortOrder: PersistenceFilter["sortOrder"]) => setState((prev) => ({ ...prev, sortCriteria, sortOrder })), [setState]);
    const onPageSizeChange = useCallback((pageSize: PersistenceFilter["pageSize"]) => setState((prev) => ({ ...prev, pageSize })), [setState]);

    const onIsFilterOpen = useCallback((state: boolean) => { onIsOpen && onIsOpen(state, "main"); }, [onIsOpen]);
    const onResetFilter = useCallback(() => setState(() => standardFilter), [setState, standardFilter]);

    const isFilterChanged = isFilterActive<PersistenceFilter>(currentState, standardFilter, ignoreKeys);

    useEffect(() =>
    {
        if (filter?.isFilterActive !== isFilterChanged) {
            onFilterActive && onFilterActive(isFilterChanged);
        }
    }, [isFilterChanged, filter, onFilterActive]);
    useEffect(() =>
    {
        if (userId) { getFilteredData(userId, currentState); }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentState, userId]);

    const isChecked = useCallback((notificationType: NotificationType) =>
    {
        const newState = { ...currentState };
        const selectedGroups = (newState.notificationType || "").split(",");
        return selectedGroups.indexOf(notificationType) >= 0;
    }, [currentState]);

    const handleValueChange = useCallback((notificationType: NotificationType, checked: boolean) =>
    {
        const newState = { ...currentState };
        const selectedGroups = newState.notificationType
            ? newState.notificationType.split(",")
            : [];

        const index = selectedGroups.indexOf(notificationType);

        if (index >= 0) selectedGroups.splice(index, 1);
        if (checked) selectedGroups.push(notificationType);

        if (selectedGroups.length) {
            newState.notificationType = selectedGroups.filter(Boolean).join(",");
        } else {
            newState.notificationType = '';
        }

        setState({ ...newState });
    }, [currentState, setState]);

    useEffect(() =>
    {
        sortCriteria.current =
            currentState.sortCriteria ? {
                key: currentState.sortCriteria,
                direction: currentState.sortOrder || "ASC"
            } : undefined;

    }, [currentState.sortCriteria, currentState.sortOrder]);


    return <div style={{ display: "flex", flexDirection: "column", height: "100%" }}>
        <FilterList
            isOpen={filter?.isFilterOpen || false}
            onIsOpen={onIsFilterOpen}
            onReset={onResetFilter}
            noPrint
        >
            <IonCol>
                <IonGrid style={{ overflowY: "auto" }}>

                    <IonRow>
                        <IonCol style={{ border: "0" }}>
                            <div style={{ width: "100%", display: "flex", justifyContent: "start" }}>
                                <IonLabel style={{ maxWidth: "fit-content" }}>{t("[REACT_NOTIFICATION_TYPE]")}</IonLabel>
                            </div>
                        </IonCol>
                    </IonRow>
                    <IonRow>
                        <IonCol size="12" style={{ display: "flex", justifyContent: "start", alignItems: "center", border: "0" }} >

                            <IonCheckbox
                                checked={isChecked('pushNotification')}
                                style={{ margin: 0, padding: 0, width: "fit-content" }}
                                onIonChange={({ detail }) => handleValueChange('pushNotification', detail.checked)}
                            >
                                {" "}
                            </IonCheckbox>
                            <IonLabel style={{ margin: "0 10px", verticalAlign: "middle" }}>{t('[REACT_PUSH_NOTIFICATION]')}</IonLabel>
                        </IonCol>
                        <IonCol size="12" style={{ display: "flex", justifyContent: "start", alignItems: "center", border: "0" }} >
                            <IonCheckbox
                                checked={isChecked('email')}
                                style={{ margin: 0, padding: 0, width: "fit-content" }}
                                onIonChange={({ detail }) => handleValueChange('email', detail.checked)}
                            >
                                {" "}
                            </IonCheckbox>
                            <IonLabel style={{ margin: "0 10px", verticalAlign: "middle" }}>{t('COM_CONTACT_EMAIL_LABEL')}</IonLabel>
                        </IonCol>

                    </IonRow>
                </IonGrid>
            </IonCol>



            <IonCol sizeLg="3" sizeMd="1" sizeSm="1">
                <div style={{ width: "100%", display: "flex", justifyContent: "center" }}>
                    <IonLabel style={{ maxWidth: "fit-content" }}>{t("TD_DATUM_DRUCK")}</IonLabel>
                </div>
                <DatePicker
                    childText={{ start: "[REACT_START]", end: "[REACT_END]" }}
                    value={dateStringToValue(currentState.created!)}
                    divider={false}
                    position="row"
                    onChange={(range) =>
                    {
                        const currentFilter = { ...currentState };
                        currentFilter.created = valueToDateString(range, timezone);
                        setState({ ...currentFilter });
                    }}
                />
            </IonCol>
        </FilterList >
        <List
            rows={result.results}
            sort={sortCriteria.current}
            onSort={onSortChange}
            onPageChange={onPageChange}
            onPageSize={onPageSizeChange}
            pageSize={result.pageSize}
            currentPage={result.currentPage}
            numberOfPages={result.totalCountOfPages}
            maxHeight={mobileView ? "86vh" : "47vh"}
            noLink
            headers={header}
        />
    </div >;
};

export default SentNotificationsLog;
